import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useCart } from '../contexts/CartContext';
import { createCheckoutSession, getStripe } from '../utils/stripe';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus, faMinus, faSpinner } from '@fortawesome/free-solid-svg-icons';

interface ShoppingCartProps {
  isOpen: boolean;
  onClose: () => void;
}

const ShoppingCart: React.FC<ShoppingCartProps> = ({ isOpen, onClose }) => {
  const { cart, updateQuantity, removeItem, clearCart, error, clearError } = useCart();
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // Close error message when the component unmounts or modal closes
  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  // Handle checkout with Stripe
  const handleCheckout = async () => {
    if (!cart || cart.items.length === 0) {
      setCheckoutError('Your cart is empty');
      return;
    }

    try {
      setIsLoading(true);
      setCheckoutError(null);

      // Create checkout session
      const session = await createCheckoutSession(cart);

      // Redirect to Stripe checkout
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Failed to load Stripe');
      }

      const { error } = await stripe.redirectToCheckout({
        sessionId: session.id
      });

      if (error) {
        throw error;
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setCheckoutError(err.message || 'Something went wrong with checkout');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Create the modal content
  const modalContent = (
    <div className="fixed inset-0 z-[9999] overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-75" onClick={onClose}></div>
      
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black text-white rounded-lg shadow-xl w-full max-w-xl flex flex-col max-h-[90vh] z-[10000] overflow-hidden border-2 border-accent"
      >
        <div className="flex justify-between items-center p-5 border-b border-gray-800">
          <h2 className="text-2xl font-bold">Your Cart</h2>
          <button 
            onClick={onClose}
            className="text-white/80 hover:text-white text-xl"
          >
            ✕
          </button>
        </div>
        
        {error && (
          <div className="bg-red-900/50 border border-red-500 p-3 mx-5 mt-5 rounded-md">
            <p className="text-red-300 text-sm flex justify-between items-center">
              <span>{error}</span>
              <button 
                onClick={clearError} 
                className="ml-2 text-red-300 hover:text-white"
                aria-label="Dismiss error"
              >
                ✕
              </button>
            </p>
          </div>
        )}
        
        <div className="flex-grow overflow-y-auto p-5 max-h-[60vh]">
          {(!cart || cart.items.length === 0) ? (
            <div className="flex flex-col items-center justify-center h-full text-white/80 py-12">
              <p className="mb-6 text-lg">Your cart is empty</p>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-accent text-white rounded-md hover:bg-accent/80 transition-colors text-lg"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <>
              {cart.items.map((item) => (
                <div key={item.id} className="flex items-center py-5 border-b border-gray-700">
                  <div className="w-24 h-24 bg-gray-800 rounded-md overflow-hidden mr-5">
                    {item.imageUrl ? (
                      <img 
                        src={item.imageUrl} 
                        alt={item.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-700 flex items-center justify-center text-white/60">
                        No Image
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-grow">
                    <h3 className="font-medium text-white text-lg">{item.name}</h3>
                    <p className="text-sm text-white/70">
                      {item.size && `Size: ${item.size}`}
                      {item.colorway && ` • ${item.colorway}`}
                    </p>
                    <p className="text-accent font-bold mt-2 text-lg">${item.price.toFixed(2)}</p>
                  </div>
                  
                  <div className="flex flex-col items-end">
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-white/60 hover:text-red-400 mb-3"
                      aria-label="Remove item"
                    >
                      <FontAwesomeIcon icon={faTrash} className="h-5 w-5" />
                    </button>
                    
                    <div className="flex items-center border border-gray-700 rounded-md bg-gray-800">
                      <button
                        onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                        className="px-3 py-2 hover:bg-gray-700 text-white"
                        disabled={item.quantity <= 1}
                        aria-label="Decrease quantity"
                      >
                        <FontAwesomeIcon icon={faMinus} className="h-3 w-3" />
                      </button>
                      
                      <span className="px-3 min-w-[30px] text-center text-white">
                        {item.quantity}
                      </span>
                      
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="px-3 py-2 hover:bg-gray-700 text-white"
                        aria-label="Increase quantity"
                      >
                        <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={clearCart}
                  className="text-red-500 text-sm hover:underline font-medium"
                >
                  Clear Cart
                </button>
              </div>
            </>
          )}
        </div>
        
        {cart && cart.items.length > 0 && (
          <div className="p-5 border-t border-gray-800">
            <div className="flex justify-between mb-4">
              <span className="font-medium text-white text-lg">Subtotal</span>
              <span className="font-bold text-accent text-xl">${cart.totalPrice.toFixed(2)}</span>
            </div>
            
            <p className="text-sm text-gray-400 mb-5">
              Shipping and taxes calculated at checkout
            </p>
            
            {checkoutError && (
              <p className="text-red-400 text-sm mb-3">{checkoutError}</p>
            )}
            
            <button
              onClick={handleCheckout}
              disabled={isLoading}
              className="w-full py-4 bg-accent text-white rounded-md font-medium hover:bg-accent/80 disabled:opacity-70 transition-colors text-lg"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                  Processing...
                </span>
              ) : (
                'Checkout'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Use a portal to render the modal at the document body level
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default ShoppingCart; 